import { Icon, Label } from '@aibees/shared/components';
import { CREDIT_USAGE_ONBOARD_BG_MP4 } from '@aibees/shared/constants';
import { classnames } from '@aibees/shared/utils';
import { Box, Button, Typography } from '@mui/material';
import { memo, useCallback, useState } from 'react';
import { CreditUsageVideoModal } from './components/credit-usage-video-modal';
import { useCreditRateCategories } from './hooks/use-credit-rate-categories';
import * as SX from './sx';
import type { CreditUsageOverviewProps as Props } from './types';

export const CreditUsageOverview = memo(({ className }: Props) => {
  const { categories } = useCreditRateCategories();
  const [videoOpen, setVideoOpen] = useState(false);

  const handleOpenVideo = useCallback(() => setVideoOpen(true), []);
  const handleCloseVideo = useCallback(() => setVideoOpen(false), []);

  return (
    <Box className={classnames('CreditUsageOverview', className)} sx={SX.root}>
      <Box sx={SX.leftColumn}>
        <Box sx={SX.headSection}>
          <Label color="primary" variant="soft" size="small">
            Credits
          </Label>
          <Typography variant="h5" color="grey.900">
            How Credits Work?
          </Typography>
          <Typography variant="body4" color="grey.600">
            Each action costs credits based on its complexity.
          </Typography>
        </Box>

        <Box sx={SX.videoWrapper}>
          <Box
            component="video"
            src={CREDIT_USAGE_ONBOARD_BG_MP4}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            sx={SX.videoBackground}
          />
          <Box sx={SX.videoContent}>
            <Box sx={SX.videoTextWrapper}>
              <Typography variant="body2" fontWeight={700} color="common.white">
                Understanding Credit Usage
              </Typography>
              <Typography variant="body5" color="grey.400">
                Learn how usage is calculated in just 60 seconds
              </Typography>
            </Box>
            <Button
              size="small"
              color="primary"
              variant="contained"
              startIcon={<Icon name="play-video" size={16} />}
              onClick={handleOpenVideo}
              disableElevation
            >
              Watch the Video
            </Button>
          </Box>
        </Box>

        <CreditUsageVideoModal open={videoOpen} onClose={handleCloseVideo} />
      </Box>

      <Box sx={SX.rightColumn}>
        <Box sx={SX.headSection}>
          <Label color="primary" variant="soft" size="small">
            Estimate
          </Label>
          <Typography variant="h5" color="grey.900">
            Estimate Your Credit Usage
          </Typography>
          <Typography variant="body4" color="grey.700">
            Get a clear estimate of the credits you&apos;ll need each month
            based on your usage.
          </Typography>
        </Box>

        <Box sx={SX.creditsContent}>
          <Box sx={SX.estimateHeader}>
            <Typography variant="body2" fontWeight={600} color="grey.900">
              Estimate Monthly Credits
            </Typography>
            {/* TODO(CU-86e003n9m): wire to the credit calculator dialog */}
            <Button
              color="primary"
              size="small"
              variant="contained"
              disabled
              startIcon={<Icon name="calculator" size={16} />}
              disableElevation
            >
              Open Credit Calculator
            </Button>
          </Box>

          <Box sx={SX.categoriesGrid}>
            {categories.map((category) => (
              <Box key={category.title} sx={SX.categoryCard}>
                <Box sx={SX.categoryHeader}>
                  <Box sx={SX.categoryIconBox}>
                    <Icon name={category.icon} size={22} color="primary.main" />
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="grey.700"
                    noWrap
                  >
                    {category.title}
                  </Typography>
                </Box>
                <Box sx={SX.categoryDivider} />
                <Box sx={SX.rowsWrap}>
                  {category.rows.map((row) => (
                    <Box key={row.type} sx={SX.creditRow}>
                      <Box sx={SX.creditRowLeft}>
                        <Icon
                          name="info-circle-outline"
                          size={18}
                          color="grey.500"
                        />
                        <Typography variant="body4" color="common.black" noWrap>
                          {row.title}
                        </Typography>
                      </Box>
                      <Box sx={SX.creditPill}>
                        <Icon name="coin" size={16} color="primary.main" />
                        <Typography variant="body4" color="primary.600">
                          x {row.credit}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

CreditUsageOverview.displayName = 'CreditUsageOverview';
